import React, { useCallback, useEffect, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import _ from 'lodash'

import LineChart from '../../linechart'
import PaceLoader from '../../paceloader'
import Heading from '../heading'
import RefreshButton from '../refresh_button'
import AnalyticsFilters from '../analytics_filters'
import ReportTableOrg from './report_org_table'
import ReportTableIndividual from './report_individual_table'

import { ApiHelper } from '../../../../helpers/apihelper'
import * as Constants from '../../../../helpers/constants'
import * as Helper from '../../../../helpers/helper'
import { buildApiFilters, getMetrics, getTableHeading, isOrgAdminView } from '../helpers'

/**
 * Port of cowebapp analytics/components/email_finder/index.js — Email Finder
 * Usage Reports (report/email_finder endpoint).
 */
const EmailFinder = ({ client, viewedUser, memberOptions }) => {

  const [metrics, setMetrics] = useState({
    total_found: 0,
    found_percentage: 0,
    conf_score: 0,
    total_request: 0
  })
  const [isPerfSummaryOpen, setIsPerfSummaryOpen] = useState(true)
  const [usageStats, setUsageStats] = useState([])
  const [usageReports, setUsageReports] = useState([])
  const [showTable, setShowTable] = useState('individual')
  const [isStatsLoading, setIsStatsLoading] = useState(false)
  const [graphData, setGraphData] = useState(null)
  const [isGraphDataLoading, setIsGraphDataLoading] = useState(false)
  const [filters, setFiltersState] = useState({})

  const summaryToggle = () => {
    setIsPerfSummaryOpen(!isPerfSummaryOpen)
  }

  const getGraphData = useCallback((data) => {
    let stat = ''
    if (Object.prototype.hasOwnProperty.call(data, 'user_stats_org')) {
      stat = data.user_stats_org[0]
    } else {
      stat = data.user_stats[0]
    }
    let graphData = {
      breakdown: data.breakdown,
      labels: [],
      values: [
        {
          key: 'found', label: 'Email Found', data: [], borderColor: '#3cba9f',
          backgroundColor: '#71d1bd0f', fill: true
        },
        {
          key: 'not_found', label: 'Email Not Found', data: [], borderColor: '#c45850',
          backgroundColor: '#d78f8914', fill: true
        },
        {
          key: 'total', label: 'Total Request', data: [], borderColor: '#3e95cd',
          backgroundColor: '#7bb6dd12', fill: true
        },
      ],
    }

    stat.stats.forEach((data) => {
      graphData.labels.push(data.date)
    })
    graphData.values.forEach((item) => {
      stat.stats.forEach((data) => {
        Object.keys(data).forEach((key) => {
          if (key === item.key) {
            item.data.push(data[key])
          }
        })
      })
    })
    return graphData
  }, [])

  const getGraphOrgData = useCallback((data) => {
    let arrStats = []
    data.user_stats.forEach((data) => {
      data.stats.forEach((st) => {
        arrStats.push(st)
      })
    })
    let objStats = {}
    arrStats.forEach(function (d) {
      if (Object.prototype.hasOwnProperty.call(objStats, d.date)) {
        objStats[d.date] = {
          found: objStats[d.date].found + d.found,
          total: objStats[d.date].total + d.total,
          not_found: objStats[d.date].not_found + d.not_found
        }
      } else {
        objStats[d.date] = {
          found: d.found,
          total: d.total,
          not_found: d.not_found
        }
      }
    })
    var userStatsOrg = [{ stats: [] }]
    for (var prop in objStats) {
      userStatsOrg[0].stats.push({
        date: prop,
        found: objStats[prop].found,
        business: objStats[prop].business,
        role: objStats[prop].role,
        total: objStats[prop].total,
        not_found: objStats[prop].not_found
      })
    }
    data.user_stats_org = userStatsOrg

    return getGraphData(data)
  }, [getGraphData])

  const setterMetrics = useCallback((stats, role) => {
    let metrics = {}
    let total_found = 0
    let total_request = 0
    let total_business = 0
    let total_role = 0
    let total_confidence_score = 0
    let tableData = []

    if (role === 'all') {
      stats.forEach((data) => {
        let total = 0
        let business = 0
        let found = 0
        let role = 0
        let confidence_score = 0
        data.stats.forEach((stat) => {
          if (stat.total > 0) {
            total = total + stat.total
            found = found + stat.found
            business = business + stat.business
            confidence_score = confidence_score + stat.confidence_score
            role = role + stat.role
          }
        })
        tableData.push({
          total: total,
          total_found: found,
          business: business,
          confidence_score: confidence_score,
          role: role,
        })
      })

      tableData.forEach((data) => {
        if (data.total > 0) {
          total_business = total_business + data.business
          total_found = total_found + data.total_found
          total_request = total_request + data.total
          total_confidence_score = total_confidence_score + data.confidence_score
          total_role = total_role + data.role
        }
      })
    } else {
      if (stats[0].stats) {
        stats = stats[0].stats
      }
      stats.forEach((data) => {
        if (data.total > 0) {
          total_business = total_business + data.business
          total_found = total_found + data.found
          total_request = total_request + data.total
          total_confidence_score = total_confidence_score + data.confidence_score
          total_role = total_role + data.role
        }
      })
    }

    metrics.total_business = total_business
    metrics.total_found = total_found
    metrics.total_request = total_request
    metrics.found_percentage = (total_found == 0 || total_request == 0) ? 0 : total_found / total_request * 100
    let score = (total_found == 0 || total_confidence_score == 0) ? 0 : total_confidence_score / total_found
    metrics.conf_score = score > 0 ? score.toFixed() : 0
    metrics.total_role = total_role

    return metrics
  }, [])

  const loadReportData = useCallback(() => {
    setIsGraphDataLoading(true)
    setIsStatsLoading(true)

    const { filters: apiFilters, selectedUserState } = buildApiFilters({
      viewedUser,
      memberFilter: filters.members,
      dateRangeFilter: filters.date_range
    })

    const queryParams = _.pick(
      { user_id: viewedUser.id, filters: JSON.stringify(apiFilters) },
      ApiHelper(client, 'EMAIL_FINDER_REPORTS').API_QUERY
    )

    ApiHelper(client, 'EMAIL_FINDER_REPORTS').API(client, queryParams)
      .then((res) => {
        setIsGraphDataLoading(false)
        if (res && res.status === Constants.CO_API.SUCCESS) {

          let usageStatsIndividualTable = selectedUserState.value.role === 'all' ?
            res.data.user_stats : res.data.user_stats[0].stats
          setUsageReports(res.data)

          setShowTable(selectedUserState.value.role === 'all' ? 'orgAllData' : 'individual')
          setUsageStats(usageStatsIndividualTable)
          setIsStatsLoading(false)
          let collectGraphData = selectedUserState.value.role === 'all'
            ? getGraphOrgData(res.data) : getGraphData(res.data)
          setGraphData(collectGraphData)
          setMetrics(setterMetrics(usageStatsIndividualTable, selectedUserState.value.role))
        }
      })
      .catch((err) => {
        setIsGraphDataLoading(false)
        setIsStatsLoading(false)
        console.log('UsageReports api erred', err)
      })
  }, [client, viewedUser, filters, getGraphData, getGraphOrgData, setterMetrics])

  useEffect(() => {
    if (!_.isEmpty(filters)) {
      loadReportData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const refreshReports = () => {
    loadReportData()
  }

  const getConfidentDetail = (conf_score) => {
    let conf_level = Helper.calculateConfidenceLevelTagFromScore(conf_score)
    let color_string = Helper.getColorTagFromConfidenceLevel(conf_level)
    return (
      <span
        ref={el => {
          if (el) {
            el.style.setProperty('color', color_string, 'important')
            el.style.setProperty('font-size', '16px', 'important')
          }
        }}
      >
        {conf_level}
      </span>
    )
  }

  const onSetFilters = (filters) => {
    setFiltersState({ ...filters })
  }

  return (
    <div className="analytics-graph-content">
      <div className="report-header col-12 d-flex justify-content-between align-items-center">
        <div className="report-date-filter">
          <Heading
            toolTipMessage="Find your credit utilization of the email finder service"
            title="Email Finder Usage Reports"
          />
        </div>
      </div>
      <div className="report-header col-12 d-flex justify-content-between align-items-center">
        <AnalyticsFilters
          client={client}
          viewedUser={viewedUser}
          memberOptions={memberOptions}
          filterState={filters}
          onSetFilters={onSetFilters}
        />
        <RefreshButton onRefresh={refreshReports} />
      </div>
      <div className="report-content ps-3 pe-3">
        <PaceLoader isLoading={isStatsLoading} />
        <div>
          <div className="d-flex report-performance-summary g-0 flex-column pt-3">
            <div className="page-report-heading">
              <h3 className="m-0">Summary</h3>
              <div className="btn-toolbar">
                <div className="btn-group">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => summaryToggle()}
                  >
                    {isPerfSummaryOpen === true ? (
                      <i className="fa fa-chevron-up"></i>
                    ) : (
                      <i className="fa fa-chevron-down"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <Collapse in={isPerfSummaryOpen}>
              <div className="graph-content">
                <div className="metrics">
                  <div className="metrics-content">
                    <div className="total-verified">
                      <span>Email Found</span>
                      {getMetrics(
                        metrics.total_found,
                        0,
                        '',
                        '',
                        'Email Found'
                      )}
                    </div>
                    <div className="total-verified">
                      <span>Email Found Percentage</span>
                      <span>
                        <div
                          className={`c100 p${Math.round(
                            metrics.found_percentage
                          )} small orange`}
                        >
                          {getMetrics(
                            metrics.found_percentage,
                            0,
                            '',
                            '%',
                            'Found Percentage'
                          )}
                          <div className="slice">
                            <div className="bar"></div>
                            <div className="fill"></div>
                          </div>
                        </div>
                      </span>
                    </div>
                    <div className="total-verified">
                      <span>Confidence Level</span>
                      {
                        getConfidentDetail(metrics.conf_score)
                      }
                    </div>
                  </div>
                </div>
                <div className="area-graph">
                  <LineChart
                    graphData={graphData}
                    isGraphDataLoading={isGraphDataLoading}
                    width={300}
                    height={250}
                    position="bottom"
                  />
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div>
          <div className="d-flex report-performance-breakdown g-0 flex-column">
            <div className="page-report-heading">
              <h3 className="m-0 d-flex align-items-center">
                {getTableHeading(isOrgAdminView(viewedUser) ? 'all' : viewedUser.user_role, usageReports.breakdown)}
              </h3>
            </div>
            {!isStatsLoading &&
              showTable === 'orgAllData'
              ? <ReportTableOrg
                userReportData={usageReports}
                tableData={usageStats}
              />
              : <ReportTableIndividual
                userReportData={usageReports}
                tableData={usageStats}
              />
            }
          </div>
        </div>
        <div className="report-footnote">
          <i
            className="fa fa-exclamation-circle"
            aria-hidden="true"
          >
          </i>
          <small className="form-text">
            Row will be supressed when no activity found for the particular
            day or hour for the chosen period
          </small>
        </div>
      </div>
    </div>
  )
}

export default EmailFinder
